import { DataTable } from "components/PortalDataTable";
import { useFormik } from "formik";
import { Column } from "primereact/column";
import { useState, useEffect, useMemo } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BsSearch, BsCheckCircle, BsPeople, BsTrash } from "react-icons/bs";
import useRevokeBooksFromUsers from "api/BookResource/useRevokeBooksFromUsers";
import useBookAssignees from "api/BookResource/useBookAssignees";
import Dialog from "components/Overlays/ResponsiveDialog";
import {
	FiArrowRight,
	FiX,
	FiUsers,
	FiCheckCircle,
	FiAlertTriangle,
	FiArrowLeft,
} from "react-icons/fi";
import StepIndicator from "components/StepIndicator";

const RevokeUsers = ({ book, className, children }) => {
	const [display, setDisplay] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);

	const steps = [
		{ label: "Select Students", icon: <BsPeople /> },
		{ label: "Confirm Revocation", icon: <FiAlertTriangle /> },
	];

	const [searchTerm, setSearchTerm] = useState("");
	const [reason, setReason] = useState("");
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [revokeResult, setRevokeResult] = useState(null);

	const { data: assignees, isLoading: isLoadingUsers } = useBookAssignees(book?.id, display);

	useEffect(() => {
		if (display) {
			setSelectedUsers([]);
			setSearchTerm("");
			setReason("");
			setRevokeResult(null);
			setCurrentStep(0);
		}
	}, [display]);

	const allStudents = assignees?.students || [];

	const users = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) {
			return allStudents;
		}
		return allStudents.filter((user) => {
			const haystack = `${user.first_name || ""} ${user.last_name || ""} ${
				user.email || ""
			}`.toLowerCase();
			return haystack.includes(term);
		});
	}, [allStudents, searchTerm]);

	const { mutateAsync, isLoading: isRevoking } = useRevokeBooksFromUsers();

	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			users: [...selectedUsers?.map((user) => user.id)],
		},
		onSubmit: async () => {
			setCurrentStep(1);
		},

		enableReinitialize: true,
	});

	const handleConfirmSubmit = async () => {
		const payload = {
			users: values.users,
			books: [book.id],
			reason: reason || undefined,
		};
		const result = await mutateAsync(payload);
		setRevokeResult(result?.data || null);
		setCurrentStep(2);
	};

	const successCount = revokeResult?.successes?.length ?? selectedUsers.length;
	const failureCount = revokeResult?.failures?.length ?? 0;

	return (
		<>
			<button
				type="button"
				className={`appearance-none border-0 ${className} `}
				onClick={() => setDisplay(true)}
			>
				{children || <BsTrash size={15} />}
			</button>

			<Dialog
				visible={display}
				onHide={() => setDisplay(false)}
				breakpoints={{
					"960px": "90vw",
					"0px": "100vw",
				}}
				className="font-sans"
				style={{ maxWidth: "900px" }}
				showHeader={false}
				contentClassName="rounded-md relative"
				blockScroll={false}
			>
				<button
					type="button"
					onClick={() => setDisplay(false)}
					className="absolute top-4 right-4 hover:bg-gray-100 rounded-full p-2 transition-colors z-10"
				>
					<FiX size={24} />
				</button>

				<div className="pt-8 pb-6">
					<div className="px-6 pb-4 border-b border-gray-200">
						<h3 className="font-cera-black text-red-700 text-2xl mb-2">
							Revoke Book from Students
						</h3>
						<p className="text-gray-600 text-sm">
							{currentStep === 0 &&
								"Select students to revoke this book from. Released copies/seats become available to assign to others."}
							{currentStep === 1 && "Review the revocation details and confirm"}
							{currentStep === 2 && "Revocation completed successfully"}
						</p>
						<StepIndicator steps={steps} currentStep={currentStep} />
					</div>

					{currentStep === 0 && (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4">
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-blue-700 mb-1">
									<FiCheckCircle size={18} />
									<span className="text-sm font-medium">Book</span>
								</div>
								<p className="text-base font-bold text-blue-900 truncate">
									{book?.title}
								</p>
							</div>
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex items-center gap-2 text-red-700 mb-1">
									<FiUsers size={18} />
									<span className="text-sm font-medium">Students Selected</span>
								</div>
								<p className="text-2xl font-bold text-red-900">
									{selectedUsers?.length || 0}
								</p>
							</div>
						</div>
					)}

					<div className="px-6 py-4">
						{currentStep === 0 && (
							<form onSubmit={handleSubmit} className="space-y-6">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Search and Select Students
									</label>
									<div className="relative">
										<input
											type="text"
											name="searchTerm"
											placeholder="Filter students by name or email..."
											className="w-full h-12 border border-gray-300 rounded-lg pr-4 pl-11 focus:ring-2 focus:ring-red-400 focus:border-transparent"
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
										<span className="absolute left-4 top-1/2 transform -translate-y-1/2">
											{isLoadingUsers ? (
												<AiOutlineLoading3Quarters
													size={18}
													className="text-gray-400 animate-spin"
												/>
											) : (
												<BsSearch size={18} className="text-gray-400" />
											)}
										</span>
									</div>
								</div>

								<div className="border border-gray-200 rounded-lg overflow-hidden">
									{users && users.length > 0 ? (
										<DataTable
											value={users}
											selection={selectedUsers}
											onSelectionChange={(e) => {
												setSelectedUsers(e.value);
												setFieldValue("users", [...e.value?.map((user) => user?.id)]);
											}}
											loading={isLoadingUsers}
											tableClassName="font-sans text-sm"
											className="w-full"
											autoLayout
										>
											<Column
												selectionMode="multiple"
												headerStyle={{ width: "3em" }}
											></Column>
											<Column field="first_name" header="First Name" sortable />
											<Column field="last_name" header="Last Name" sortable />
											<Column field="email" header="Email" sortable />
										</DataTable>
									) : !isLoadingUsers ? (
										<div className="py-12 text-center text-gray-500">
											<FiUsers size={48} className="mx-auto mb-3 text-gray-300" />
											<p className="font-medium">No students found</p>
											<p className="text-sm">
												{searchTerm
													? "Try a different search term"
													: "No students have this book directly assigned. Students with group-only access can be revoked via Revoke from subject groups."}
											</p>
										</div>
									) : null}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Reason (optional)
									</label>
									<input
										type="text"
										placeholder="e.g. End of term"
										className="w-full h-12 border border-gray-300 rounded-lg px-4 focus:ring-2 focus:ring-red-400 focus:border-transparent"
										value={reason}
										onChange={(e) => setReason(e.target.value)}
										maxLength={255}
									/>
								</div>

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={isRevoking || selectedUsers.length === 0 || !book}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										Review Revocation
										<FiArrowRight size={20} />
									</button>
								</div>
							</form>
						)}

						{currentStep === 1 && (
							<div className="space-y-6">
								<div className="bg-red-50 border border-red-200 rounded-lg p-6">
									<div className="flex items-start gap-4">
										<div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
											<FiAlertTriangle size={24} className="text-red-600" />
										</div>
										<div className="flex-1">
											<h4 className="font-medium text-red-900 mb-2">
												Confirm Revocation
											</h4>
											<p className="text-sm text-red-700 mb-4">
												You are about to revoke "{book?.title}" from {selectedUsers.length}{" "}
												student{selectedUsers.length !== 1 ? "s" : ""}. They will lose
												access and the released copies/seats will become available to
												assign to others.
											</p>
											<div className="text-sm">
												<span className="font-medium text-gray-700">Students:</span>
												<ul className="mt-1 space-y-1">
													{selectedUsers.map((user) => (
														<li key={user.id} className="text-gray-600">
															• {user.first_name} {user.last_name}
														</li>
													))}
												</ul>
											</div>
										</div>
									</div>
								</div>

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setCurrentStep(0)}
										className="flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
									>
										<FiArrowLeft size={20} />
										Back to Selection
									</button>
									<button
										type="button"
										onClick={handleConfirmSubmit}
										disabled={isRevoking}
										className="flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
									>
										{isRevoking ? (
											<>
												<AiOutlineLoading3Quarters size={20} className="animate-spin" />
												Revoking...
											</>
										) : (
											<>
												Confirm Revocation
												<BsTrash size={18} />
											</>
										)}
									</button>
								</div>
							</div>
						)}

						{currentStep === 2 && (
							<div className="text-center space-y-6">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
									<BsCheckCircle size={32} className="text-green-600" />
								</div>
								<div>
									<h4 className="font-medium text-gray-900 mb-2">
										{failureCount > 0 && successCount > 0
											? "Revocation Partially Completed"
											: "Revocation Successful!"}
									</h4>
									<p className="text-sm text-gray-600">
										"{book?.title}" has been revoked from {successCount} student
										{successCount !== 1 ? "s" : ""}.
										{failureCount > 0 &&
											` ${failureCount} could not be revoked.`}
									</p>
									{failureCount > 0 && (
										<ul className="mt-3 text-sm text-red-600 text-left max-w-md mx-auto space-y-1">
											{revokeResult.failures.map((failure, index) => (
												<li key={`${failure.user_id}-${failure.book_id}-${index}`}>
													• Student #{failure.user_id}: {failure.reason}
												</li>
											))}
										</ul>
									)}
								</div>
								<div className="flex justify-center gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setDisplay(false)}
										className="px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors font-medium"
									>
										Done
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default RevokeUsers;
